import {
  Component,
  Input,
  OnInit,
  inject,
  input,
  output,
  OnDestroy,
  signal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { DecimalPipe } from "@angular/common";

import { quoteType } from "../quote-form/quote-type.const";
import { quoteOptionsCurrencyType } from "./quote-options-currency-type.const";
import { Quote } from "../quote.type";
import { ManualQuote } from "../models/quote-manual.model";
import { Observable, of, Subscription } from "rxjs";
import { catchError, filter, map, startWith, switchMap } from "rxjs/operators";
import { QuoteRepository } from "../quote.repository";
import { SelectItem } from "primeng/api";
import { FclTariff } from "../models/quote-fcl-tariff.model";
import { Location } from "../../location/location.model";
import { FclQuote } from "../models/quote-fcl.model";
import { LclQuote } from "../models/quote-lcl.model";
import { AirQuote } from "../models/quote-air.model";
import {
  currenciesList,
  currencies,
} from "../../shared/currencies/currencies.const";
import { BaseStandardQuote } from "../models/quote-base-standard.model";
import { BaseLclQuote } from "../models/quote-base-lcl.model";
import { freightRateOrder } from "../../shared/freight-rate/freight-rate-order.const";
import { LtlQuote } from "../models/quote-ltl.model";
import { EquipmentQuantity } from "../models/equipment-quantity.model";
import { QuoteLocation } from "../models/quote-location.model";
import { AirTariffQuote } from "../models/quote-airtariff.model";
import { UserService } from "app/user/user.service";
import { MultiQuote } from "../models/quote-multi.model";
import { FtlQuote } from "../models/quote-ftl.model";
import { PtlQuote } from "../models/quote-ptl.model";
import { LocaleSelectComponent } from "../../ui/dropdown/locale-select/locale-select.component";
import { Bind } from "primeng/bind";
import { Select } from "primeng/select";
import { FormsModule } from "@angular/forms";
import { Checkbox } from "primeng/checkbox";
import { ManualQuoteTypeSelectComponent } from "../../ui/dropdown/manual-quote-type-select/manual-quote-type-select.component";
import { MultiSelect } from "primeng/multiselect";
import { Tooltip } from "primeng/tooltip";
import { RadioButton } from "primeng/radiobutton";
import { EquipmentQuantityComponent } from "../quote-equipment/equipment-quantity.component";
import { QuoteInsuranceComponent } from "../quote-form/quote-insurance/quote-insurance.component";
import { User } from "../../user/user.model";
import { ButtonDirective } from "primeng/button";
import { NotifyService } from "../../notify/notify.service";
import { TOAST } from "../../notify/toast/toast-type.enum";
import {
  QuoteExchangeRate,
  QuoteExchangeRates,
} from "../models/quote-exchange-rates.model";
import { EventService } from "../../shared/event/event.service";
import { eventType } from "../../notify/event.const";
import { BaseQuote } from "../models/quote-base.model";

@Component({
  selector: "app-quote-options",
  templateUrl: "./quote-options.component.html",
  styleUrls: ["./quote-options.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    LocaleSelectComponent,
    Bind,
    Select,
    FormsModule,
    Checkbox,
    ManualQuoteTypeSelectComponent,
    MultiSelect,
    Tooltip,
    RadioButton,
    EquipmentQuantityComponent,
    QuoteInsuranceComponent,
    ButtonDirective,
    DecimalPipe,
  ],
})
export class QuoteOptionsComponent implements OnInit, OnDestroy {
  private quoteRepository = inject(QuoteRepository);
  private userService = inject(UserService);
  private notifyService = inject(NotifyService);
  private eventService = inject(EventService);

  public readonly quote = input<Quote>(undefined);
  @Input() public showCurrencyOptions = false;
  public readonly routeCount = input(0);
  @Input() public showCarrierOptions = true;
  public readonly showTariffOptions = input(false);
  @Input() public termsEditorEnabled = false;
  @Input() public user: User;
  // Scoped to the insurance group rather than named `disabled`: it gates that
  // one control, not the option list around it.
  public readonly insuranceDisabled = input<boolean>(false);
  public readonly quoteChange = output<any>();
  public readonly termsChange = output<boolean>();
  public readonly insuranceApply = output<{
    insurance: {
      showInsurance: boolean;
      insuranceCargoValue: number;
      insuranceFreightValue: number;
      insuranceRatePercent: number;
      insuranceCurrency: string;
    };
  }>();
  public readonly locationSelect = output<{
    originLocations?: Location[];
    destinationLocations?: Location[];
  }>();

  public offerLanguage: string = null;
  public showInternalNotes = false;
  public showExternalNotes = false;
  public showCarrierName = false;
  public showServiceNameForLocalCharges = false;
  public showRateSurcharges = false;
  public showNoMarginSurcharges = false;
  public showInvoiceInfo = false;
  public invoiceCurrency = null;
  public showRateName = false;
  public showAllLocationsInOneSheet = false;
  public showRateValidity = false;
  public groupRoutes = false;
  public specifyLocations = false;
  public groupOriginSurcharges = false;
  public groupDestinationSurcharges = false;
  public selectedOriginSurchargeCurrency: string = null;
  public selectedDestinationSurchargeCurrency: string = null;
  public quoteType = quoteType;
  public manualQuoteType = null;
  public quoteOptionsCurrencyType = quoteOptionsCurrencyType;
  public totalType = quoteOptionsCurrencyType.none;
  public showTotal = false;
  public selectedTotalCurrency: string = null;
  public selectedChargesCurrency: string = null;
  public equipmentQuantity: Map<string, EquipmentQuantity>;
  public showGrandTotal = false;
  public grandTotalCurrency: string = null;
  public currencySelectList = [];
  public originLocationList: SelectItem[] = [];
  public destinationLocationList: SelectItem[] = [];
  public selectedOriginLocations: Location[] = [];
  public selectedDestinationLocations: Location[] = [];
  public hasMoreThanOneLocation = false;
  public summaryCurrency: string = null;

  public selectedFreightRateOrder: string = null;
  public selectedFreightRateSecondOrder: string = null;
  public freightRateOrderList: SelectItem[] = [];
  public freightRateSecondOrderList: SelectItem[] = [];
  public showAllLocationsInOneSheetOptions: SelectItem[] = [
    { label: "Show all locations in one sheet", value: true },
    { label: "Show all locations in separate sheets", value: false },
  ];
  public readonly exchangeRates = signal<QuoteExchangeRates | null>(null);
  private subscription = new Subscription();

  ngOnInit() {
    this.buildCurrencyList();
    this.listenToExchangeRates();

    const quote = this.quote();
    if (quote instanceof BaseStandardQuote) {
      this.offerLanguage = quote.getOfferLanguage();
    }

    if (
      quote instanceof BaseStandardQuote &&
      !(quote instanceof LtlQuote) &&
      !(quote instanceof MultiQuote) &&
      !(quote instanceof FtlQuote) &&
      !(quote instanceof PtlQuote)
    ) {
      this.showCarrierName = quote.getShowCarrier();
      this.hasMoreThanOneLocation = this.checkMoreThanOneLocation();
    }

    if (
      quote instanceof BaseStandardQuote &&
      !(quote instanceof LtlQuote) &&
      !(quote instanceof AirTariffQuote)
    ) {
      this.totalType = this.getTotalType(quote);
      this.groupRoutes = quote.getGroupRoutes();
      this.selectedTotalCurrency = quote.getTotalCurrency()
        ? quote.getTotalCurrency()
        : this.currencySelectList[0].value;
      if (quote instanceof FclQuote || quote instanceof BaseLclQuote) {
        this.selectedChargesCurrency = quote.getChargesCurrency()
          ? quote.getChargesCurrency()
          : this.currencySelectList[0].value;
      }
      this.selectedOriginSurchargeCurrency =
        quote.getGroupOriginSurchargesCurrency()
          ? quote.getGroupOriginSurchargesCurrency()
          : this.currencySelectList[0].value;
      this.selectedDestinationSurchargeCurrency =
        quote.getGroupDestinationSurchargesCurrency()
          ? quote.getGroupDestinationSurchargesCurrency()
          : this.currencySelectList[0].value;
      this.groupOriginSurcharges = quote.getGroupOriginSurcharges();
      this.groupDestinationSurcharges = quote.getGroupDestinationSurcharges();
    } else {
      this.showCarrierOptions = false;
      this.showRateSurcharges = false;
      this.showCurrencyOptions = false;
    }

    if (quote instanceof LtlQuote) {
      this.showCarrierOptions = true;
      this.showCarrierName = quote.getShowCarrier();
      this.showTotal = quote.getShowTotal();
      this.selectedTotalCurrency = quote.getTotalCurrency()
        ? quote.getTotalCurrency()
        : this.currencySelectList[0].value;
      this.showRateSurcharges = quote.getShowRateSurcharges();
      this.showNoMarginSurcharges = quote.getShowNoMarginSurcharges();
    }

    if (
      quote instanceof BaseStandardQuote &&
      !(quote instanceof AirTariffQuote)
    ) {
      this.showCarrierName = quote.getShowCarrier();
      this.showInvoiceInfo = quote.getShowInvoiceInfo();
      this.invoiceCurrency = quote.getInvoiceCurrency();
    }

    if (quote instanceof AirTariffQuote) {
      this.showCarrierOptions = true;
    }

    if (quote instanceof ManualQuote) {
      this.manualQuoteType = quote.getManualQuoteType();
    }

    if (quote instanceof FclQuote) {
      this.showServiceNameForLocalCharges =
        quote.getShowServiceNameForLocalCharges();
      const freightRateOrderArray = quote.getFreightRateOrder();
      this.selectedFreightRateOrder = freightRateOrderArray[0];
      this.selectedFreightRateSecondOrder = freightRateOrderArray[1];
      this.showRateSurcharges = quote.getShowRateSurcharges();
      this.buildFreightRateOrderList();
      this.showGrandTotal = quote.getShowGrandTotal();
      this.grandTotalCurrency = quote.getGrandTotalCurrency();
      this.equipmentQuantity = quote.getEquipmentQuantity();
      this.showRateName = quote.getShowRateName();
      this.showRateValidity = quote.getShowRateValidity();
    }

    if (quote instanceof LclQuote) {
      this.showRateValidity = quote.getShowRateValidity();
    }

    if (quote instanceof FclTariff) {
      this.showRateSurcharges = quote.getShowRateSurcharges();
      this.showInternalNotes = quote.getShowInternalNotes();
      this.showExternalNotes = quote.getShowExternalNotes();
      this.showRateName = quote.getShowRateName();
      this.showAllLocationsInOneSheet = quote.getShowAllLocationsInOneSheet();
      this.buildLocationList();
    }

    if (quote instanceof MultiQuote) {
      this.summaryCurrency = quote.getSummaryCurrency();
      this.showCarrierName = quote.getShowCarrier();
      this.totalType = this.getTotalType(quote);
      this.selectedTotalCurrency = quote.getTotalCurrency()
        ? quote.getTotalCurrency()
        : this.currencySelectList[0].value;
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public updateGrandTotalParams($event): void {
    this.equipmentQuantity = $event.equipmentQuantity;
    this.showGrandTotal = $event.showGrandTotal;
    this.grandTotalCurrency = $event.grandTotalCurrency;
    this.dispatchQuoteEvents();
  }

  public dispatchQuoteEvents(): void {
    const payload = {
      offerLanguage: this.offerLanguage,
      showCarrier: this.showCarrierName,
      showServiceNameForLocalCharges: this.showServiceNameForLocalCharges,
      showTotal: false,
      showInland: false,
      showChargesInTotalCurrency: false,
      chargesCurrency: null,
      totalCurrency: null,
      showInternalNotes: null,
      showExternalNotes: null,
      showRateName: null,
      showAllLocationsInOneSheet: null,
      showRateValidity: null,
      showInvoiceInfo: null,
      invoiceCurrency: null,
      groupRoutes: this.groupRoutes,
      groupOriginSurcharges: this.groupOriginSurcharges,
      groupDestinationSurcharges: this.groupDestinationSurcharges,
      groupOriginSurchargesCurrency: this.selectedOriginSurchargeCurrency,
      groupDestinationSurchargesCurrency:
        this.selectedDestinationSurchargeCurrency,
      freightRateOrder: null,
      showRateSurcharges: null,
      showNoMarginSurcharges: null,
      manualQuoteType: null,
      equipmentQuantity: null,
      showGrandTotal: null,
      grandTotalCurrency: null,
      summaryCurrency: null,
    };
    switch (this.totalType) {
      case quoteOptionsCurrencyType.subTotals:
        payload.showTotal = true;
        payload.totalCurrency = null;
        break;
      case quoteOptionsCurrencyType.subTotalsConverted:
        payload.showTotal = true;
        payload.totalCurrency = this.selectedTotalCurrency;
        break;
      case quoteOptionsCurrencyType.subTotalsConvertedWithFreight:
        payload.showInland = true;
        payload.totalCurrency = this.selectedTotalCurrency;
        break;
      case quoteOptionsCurrencyType.chargesConverted:
        payload.showChargesInTotalCurrency = true;
        break;
      case quoteOptionsCurrencyType.none:
      default:
    }

    if (this.showChargesInTotalCurrencyOption()) {
      payload.chargesCurrency = this.selectedChargesCurrency;
    }

    const quote = this.quote();
    if (quote instanceof BaseStandardQuote) {
      payload.invoiceCurrency = this.invoiceCurrency;
      payload.showInvoiceInfo = this.showInvoiceInfo;
      if (!this.invoiceCurrency) {
        payload.invoiceCurrency = [currencies.usd.value];
      }
    }

    if (quote instanceof LtlQuote) {
      payload.showRateSurcharges = this.showRateSurcharges;
      payload.showNoMarginSurcharges = this.showNoMarginSurcharges;
      if (this.showTotal) {
        payload.showTotal = true;
        payload.totalCurrency = this.selectedTotalCurrency;
      }
    }

    if (quote instanceof ManualQuote) {
      payload.manualQuoteType = this.manualQuoteType;
    }

    if (quote instanceof FclQuote) {
      payload.showServiceNameForLocalCharges =
        this.showServiceNameForLocalCharges;
      payload.showRateSurcharges = this.showRateSurcharges;
      payload.freightRateOrder = [];
      if (this.selectedFreightRateOrder) {
        payload.freightRateOrder.push(this.selectedFreightRateOrder);
      }
      if (this.selectedFreightRateSecondOrder) {
        payload.freightRateOrder.push(this.selectedFreightRateSecondOrder);
      }
      payload.equipmentQuantity = this.showGrandTotal
        ? this.equipmentQuantity
        : new Map<string, EquipmentQuantity>();
      payload.showGrandTotal = this.showGrandTotal;
      payload.grandTotalCurrency = this.grandTotalCurrency;
      if (!this.grandTotalCurrency) {
        payload.grandTotalCurrency = currencies.usd.value;
      }
      if (!this.showCarrierName) {
        this.showServiceNameForLocalCharges = false;
        payload.showServiceNameForLocalCharges =
          this.showServiceNameForLocalCharges;
      }
      payload.showRateName = this.showRateName;
      payload.showRateValidity = this.showRateValidity;
    }

    if (quote instanceof LclQuote) {
      payload.showRateValidity = this.showRateValidity;
    }

    if (quote instanceof FclTariff) {
      payload.showInternalNotes = this.showInternalNotes;
      payload.showExternalNotes = this.showExternalNotes;
      payload.showRateName = this.showRateName;
      payload.showRateSurcharges = this.showRateSurcharges;
      payload.showAllLocationsInOneSheet = this.showAllLocationsInOneSheet;
    }

    if (quote instanceof AirTariffQuote) {
      payload.showCarrier = this.showCarrierName;
    }

    if (quote instanceof MultiQuote) {
      payload.summaryCurrency = this.selectedTotalCurrency;
      payload.showCarrier = this.showCarrierName;
    }

    this.quoteChange.emit(payload);
  }

  public isCurrencyGroupingEnabled() {
    return this.showCurrencyOptions && this.routeCount() < 2;
  }

  public isEquipmentQuantityEnabled() {
    return (
      this.showCurrencyOptions &&
      !this.hasMoreThanOneLocation &&
      this.quote().getQuoteType() === quoteType.fcl
    );
  }

  public dispatchTermsEvents(): void {
    this.termsChange.emit(this.termsEditorEnabled);
  }

  public showFclTariffQuoteOptions() {
    return this.quote() instanceof FclTariff;
  }

  public showAirTariffQuoteOptions() {
    return this.quote() instanceof AirTariffQuote;
  }

  public showMultiQuoteOptions() {
    return this.quote() instanceof MultiQuote;
  }

  public showChargesInTotalCurrencyOption() {
    const quote = this.quote();

    return (
      quote instanceof FclQuote ||
      quote instanceof LclQuote ||
      quote instanceof AirQuote
    );
  }

  public showManualQuoteOptions() {
    return this.quote() instanceof ManualQuote;
  }

  public showFtlQuoteOptions() {
    return this.quote() instanceof FtlQuote;
  }

  public showPtlQuoteOptions() {
    return this.quote() instanceof PtlQuote;
  }

  public showBaseStandardQuoteOptions() {
    return this.quote() instanceof BaseStandardQuote;
  }

  public showFclQuoteOptions() {
    return this.quote() instanceof FclQuote;
  }

  public showLclQuoteOptions() {
    return this.quote() instanceof LclQuote;
  }

  public showLtlQuoteOptions() {
    return this.quote() instanceof LtlQuote;
  }

  public showModifyTermsAndConditions(): boolean {
    const quote = this.quote();
    if (quote instanceof FclTariff) {
      return (
        quote.getClients().length === 1 && !this.showAllLocationsInOneSheet
      );
    } else {
      return quote.getClients().length === 1;
    }
  }

  public haveLocationsSelected() {
    return (
      this.selectedOriginLocations.length &&
      this.selectedDestinationLocations.length
    );
  }

  public exchangeRateEquation(
    exchangeRate: QuoteExchangeRate,
    spread: number,
  ): string {
    return `${this.formatExchangeRate(exchangeRate.rate)} × (1 + ${spread}%) = ${this.formatExchangeRate(exchangeRate.rateWithSpread)}`;
  }

  public copyToClipboard(value: number): void {
    navigator.clipboard?.writeText(this.formatExchangeRate(value)).then(
      () => this.notifyService.toast(TOAST.SUCCESS, "Exchange rate copied"),
      () =>
        this.notifyService.toast(
          TOAST.ERROR,
          "Could not copy the exchange rate",
        ),
    );
  }

  public specifyLocationsChanged() {
    if (!this.specifyLocations) {
      this.selectedOriginLocations = [];
      this.selectedDestinationLocations = [];
      this.originLocationChange();
      this.destinationLocationChange();
    }
  }

  updateAllLocationsInOnSheet(): void {
    if (!this.showRateName || !this.showExternalNotes) {
      this.showAllLocationsInOneSheet = false;
    }
  }

  public originLocationChange() {
    this.locationSelect.emit({ originLocations: this.selectedOriginLocations });
  }

  public destinationLocationChange() {
    this.locationSelect.emit({
      destinationLocations: this.selectedDestinationLocations,
    });
  }

  private checkMoreThanOneLocation(): boolean {
    const quote = this.quote();
    if (quote instanceof BaseStandardQuote) {
      const originLocationIDs = this.getLocationIds(quote.getOriginLocations());
      if (this.checkIfDuplicateExists(originLocationIDs)) {
        return true;
      }

      const destinationLocationIDs = this.getLocationIds(
        quote.getDestinationLocations(),
      );
      if (this.checkIfDuplicateExists(destinationLocationIDs)) {
        return true;
      }
    }

    return false;
  }

  // A leg whose address has no resolved location yet is skipped, so two such
  // legs do not read as the same location.
  private getLocationIds(legs: QuoteLocation[]): number[] {
    return legs
      .map((leg) => leg.getLocation()?.getId())
      .filter((id) => id !== null && id !== undefined);
  }

  private checkIfDuplicateExists(arr): boolean {
    return new Set(arr).size !== arr.length;
  }

  private getTotalType(quote): number {
    if (quote.getShowChargesInTotalCurrency() && quote.getChargesCurrency()) {
      return quoteOptionsCurrencyType.chargesConverted;
    }
    if (this.hasMoreThanOneLocation) {
      return quoteOptionsCurrencyType.none;
    }
    if (quote.getShowTotal() && !quote.getTotalCurrency()) {
      return quoteOptionsCurrencyType.subTotals;
    }
    if (quote.getShowTotal() && quote.getTotalCurrency()) {
      return quoteOptionsCurrencyType.subTotalsConverted;
    }
    if (quote.getShowInland()) {
      return quoteOptionsCurrencyType.subTotalsConvertedWithFreight;
    }

    return quoteOptionsCurrencyType.none;
  }

  // Reloaded once a save lands rather than on every new quote input: the form
  // replaces its quote before the PATCH is sent, so the input runs ahead of the backend.
  private listenToExchangeRates(): void {
    this.subscription.add(
      this.eventService.eventBus
        .pipe(
          filter((incoming) => incoming.event === eventType.quoteUpdated),
          map((incoming) =>
            incoming.data instanceof BaseQuote ? incoming.data : this.quote(),
          ),
          startWith(this.quote()),
          switchMap((quote) => this.loadExchangeRates(quote)),
        )
        .subscribe((exchangeRates) => this.exchangeRates.set(exchangeRates)),
    );
  }

  private loadExchangeRates(
    quote: BaseQuote,
  ): Observable<QuoteExchangeRates | null> {
    if (
      !(quote instanceof BaseStandardQuote) ||
      quote instanceof MultiQuote ||
      !quote.getId()
    ) {
      return of(null);
    }

    return this.quoteRepository
      .getExchangeRates(quote.getId())
      .pipe(catchError(() => of(null)));
  }

  private formatExchangeRate(value: number): string {
    return String(Number(value.toFixed(6)));
  }

  private buildCurrencyList(): void {
    currenciesList.forEach((currKey) => {
      this.currencySelectList.push({
        label: currencies[currKey].label,
        value: currencies[currKey].value,
      });
    });
  }

  private loadSelectedLocations() {
    const quote = this.quote();
    if (quote instanceof FclTariff) {
      this.selectedOriginLocations = quote.getOriginLocations();
      this.selectedDestinationLocations = quote.getDestinationLocations();
      this.specifyLocations =
        this.selectedDestinationLocations.length > 0 ||
        this.selectedOriginLocations.length > 0;
    }
  }

  private buildLocationList() {
    this.subscription.add(
      this.quoteRepository
        .getFclTariffContractLocations(this.quote().getId())
        .subscribe(
          (data: {
            originLocations: Location[];
            destinationLocations: Location[];
          }) => {
            data.originLocations.forEach((element) => {
              this.originLocationList.push({
                label: element.getName(),
                value: element,
              });
            });
            data.destinationLocations.forEach((element) => {
              this.destinationLocationList.push({
                label: element.getName(),
                value: element,
              });
            });

            this.loadSelectedLocations();
          },
        ),
    );
  }

  private buildFreightRateOrderList() {
    Object.keys(freightRateOrder).map((key) => {
      this.freightRateOrderList.push({
        label: freightRateOrder[key],
        value: key,
      });
    });
    this.freightRateSecondOrderList = [...this.freightRateOrderList];
    this.freightRateSecondOrderList.unshift({
      label: "None",
      value: null,
    });
  }
}
